import LFCBaseCharts from '_components/charts/base/LFCBaseCharts';
import {fmtYYYYMMDDHHmmss} from '_logics/LFCFormatUtil';
import {useParseWithSettings} from '_logics/LFCParseUtil';
import dayjs from 'dayjs';
import {
  CloudDistanceDigGraphBaseData,
  SearchType
} from 'pages/lq-job/cloud-distance/components/cloud-distance-dig/types';
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {blue, lightBlue, pink, red} from '@mui/material/colors';

/**
 * 引数
 */
interface Props {
  title: string;
  source: CloudDistanceDigGraphBaseData;
  x?: {
    type: 'time' | 'value' | 'category';
    dsColumn: string;
  };
  y?: {
    type: 'time' | 'value' | 'category';
    dsColumn: string;
  };
  exportFilename?: string;
  height?: string;
  zoomChartId?: string;
  zoomedChartId?: string;
  zoomCharts?: (zoomChartId?: string) => void;
  zoomChartsClose?: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void;
  searchType: number;
}

/**
 * LFCChartsCloudDistanceTrend
 * @param props
 * @returns
 */
const LFCChartsCloudDistanceTrend = React.memo(
  ({
    title,
    source,
    x,
    y,
    exportFilename,
    height,
    zoomChartId,
    zoomedChartId,
    zoomCharts,
    zoomChartsClose,
    searchType
  }: Props) => {
    const {t} = useTranslation();
    const {parseSize} = useParseWithSettings();
    const [boxData, setBoxData] = useState([]);
    const [exportFields, setExportFields] = useState({});
    const [exportData, setExportData] = useState<any>([]);

    const [legendSelected, setLegendSelected] = useState({
      最大値: true,
      最小値: true,
      最大値箱ひげ図: true,
      最小値箱ひげ図: true,
      上限: true,
      下限: true
    });

    const CalcQ = useCallback((data: number[], coefficient: number): number => {
      const data_s = data.sort((a: number, b: number) => a - b);
      const pos = (data_s.length - 1) * coefficient;
      const base = Math.floor(pos);
      const rest = pos - base;
      if (data_s[base + 1] !== undefined) {
        return data_s[base] + rest * (data_s[base + 1] - data_s[base]);
      } else {
        return data_s[base];
      }
    }, []);

    const CalcQuartile = useCallback(
      (data: number[]): Array<number> => {
        if (data.length === 0) {
          return [];
        }
        const min = Math.min(...data.map((a: any) => Number(a)));
        const q1 = CalcQ(data, 0.25);
        const q2 = CalcQ(data, 0.5);
        const q3 = CalcQ(data, 0.75);
        const max = Math.max(...data.map((a: any) => Number(a)));

        // [min,  Q1,  median (or Q2),  Q3,  max]
        return [min, q1, q2, q3, max];
      },
      [CalcQ]
    );

    useEffect(() => {
      //ボックスデータの算出
      let tmpOutPut: any = [];
      tmpOutPut[0] = [];
      tmpOutPut[1] = [];
      tmpOutPut[2] = [];

      if (searchType === SearchType.WORK) {
        //何もしない
      } else {
        source.xlist.forEach((b: string, index: number) => {
          tmpOutPut[0].push(CalcQuartile(source.data_upper_distance[index] as number[]));
          tmpOutPut[1].push(CalcQuartile(source.data_lower_distance[index] as number[]));
        });
      }
      setBoxData(tmpOutPut);

      setLegendSelected({
        最大値: true,
        最小値: true,
        最大値箱ひげ図: true,
        最小値箱ひげ図: true,
        上限: true,
        下限: true
      });
    }, [source]);

    useEffect(() => {
      if (boxData.length === 0) return;

      if (searchType === SearchType.WORK) {
        //検査ごと
        setExportFields({
          シリアルナンバー: t('シリアルナンバー'),
          検査日時: t('検査日時'),
          検査部位: t('検査部位'),
          最大値: t('最大値'),
          最小値: t('最小値'),
          上限: t('上限'),
          下限: t('下限')
        });

        const _exportData = source?.xlist.map((xval: string, i: number) => {
          return {
            シリアルナンバー: source.detail[i].serial || source.detail[i].uuid,
            検査日時: fmtYYYYMMDDHHmmss(source.detail[i].lq_time),
            検査部位: source.map,
            最大値: parseSize((source.data_upper_distance[i] as number) || 0),
            最小値: parseSize((source.data_lower_distance[i] as number) || 0),
            上限: parseSize((source.data_upper_limits[i] as number) || 0),
            下限: parseSize((source.data_lower_limits[i] as number) || 0)
          };
        });
        setExportData(_exportData);
      } else {
        setExportFields({
          検査日時: t('検査日時'),
          検査部位: t('検査部位'),
          最大値_最大: t('最大値　最大'),
          最大値_Q3: t('最大値　Q3'),
          最大値_中央: t('最大値　中央'),
          最大値_Q1: t('最大値　Q1'),
          最大値_最小: t('最大値　最小'),
          最小値_最大: t('最小値　最大'),
          最小値_Q3: t('最小値　Q3'),
          最小値_中央: t('最小値　中央'),
          最小値_Q1: t('最小値　Q1'),
          最小値_最小: t('最小値　最小'),
          上限: t('上限'),
          下限: t('下限')
        });

        const _exportData = source?.xlist
          .map((xval: string, i: number) => {
            if (!Array.isArray(boxData[0][i])) return null;
            if ((boxData[0][i] as any[]).length === 0 || (boxData[1][i] as any).length === 0)
              return null;

            return {
              検査日時: fmtYYYYMMDDHHmmss(xval),
              検査部位: source.map,
              最大値_最大: parseSize(boxData[0][i][4]),
              最大値_Q3: parseSize(boxData[0][i][3]),
              最大値_中央: parseSize(boxData[0][i][2]),
              最大値_Q1: parseSize(boxData[0][i][1]),
              最大値_最小: parseSize(boxData[0][i][0]),
              最小値_最大: parseSize(boxData[1][i][4]),
              最小値_Q3: parseSize(boxData[1][i][3]),
              最小値_中央: parseSize(boxData[1][i][2]),
              最小値_Q1: parseSize(boxData[1][i][1]),
              最小値_最小: parseSize(boxData[1][i][0]),
              上限: parseSize(source.limits_max),
              下限: parseSize(source.limits_min)
            };
          })
          .filter((item: any) => item !== null); // nullの行を除外
        setExportData(_exportData);
      }
    }, [boxData]);

    return (
      <div>
        <LFCBaseCharts
          option={{
            title: {
              text: title
            },
            grid: {
              top: 40,
              left: 50,
              right: 5,
              bottom: 60
            },
            legend: {
              id: 1,
              type: 'scroll',
              bottom: 0,
              data:
                searchType === SearchType.WORK
                  ? [t('最大値'), t('最小値'), t('上限'), t('下限')]
                  : [t('最大値箱ひげ図'), t('最小値箱ひげ図'), t('上限'), t('下限')],
              selected: legendSelected
            },
            tooltip: {
              trigger: 'axis', // 軸に沿ってtooltipを表示するために'axis'を使用
              formatter: function (params: any) {
                if (searchType === SearchType.WORK) {
                  return (
                    t('連番') +
                    ':' +
                    params[0].name +
                    '<br/>' +
                    t('シリアルナンバー') +
                    ':' +
                    (source.detail[params[0].dataIndex].serial ||
                      source.detail[params[0].dataIndex].uuid) +
                    '<br/>' +
                    t('検査日時') +
                    ':' +
                    dayjs(source.detail[params[0].dataIndex].lq_time).format(
                      'YYYY-MM-DD HH:mm:ss'
                    ) +
                    '<br/>' +
                    t('最大値') +
                    ':' +
                    (isNaN(params[0].value) ? 'データなし' : Number(params[0].value).toFixed(2)) +
                    '<br/>' +
                    t('最小値') +
                    ':' +
                    (isNaN(params[1].value) ? 'データなし' : Number(params[1].value).toFixed(2)) +
                    '<br/>' +
                    '----' +
                    '<br/>' +
                    t('上限') +
                    ':' +
                    (isNaN(params[2].value) ? 'データなし' : Number(params[2].value).toFixed(2)) +
                    '<br/>' +
                    t('下限') +
                    ':' +
                    (isNaN(params[3].value) ? 'データなし' : Number(params[3].value).toFixed(2))
                  );
                }
                return (
                  params[0].name +
                  '<br/><br/>' +
                  t('最大') +
                  ':' +
                  (isNaN(params[0].value[5])
                    ? 'データなし'
                    : Number(params[0].value[5]).toFixed(2)) +
                  '<br/>' +
                  'Q3：' +
                  (isNaN(params[0].value[4])
                    ? 'データなし'
                    : Number(params[0].value[4]).toFixed(2)) +
                  '<br/>' +
                  t('中央') +
                  ' (or Q2)：' +
                  (isNaN(params[0].value[3])
                    ? 'データなし'
                    : Number(params[0].value[3]).toFixed(2)) +
                  '<br/>' +
                  'Q1：' +
                  (isNaN(params[0].value[2])
                    ? 'データなし'
                    : Number(params[0].value[2]).toFixed(2)) +
                  '<br/>' +
                  t('最小') +
                  ':' +
                  (isNaN(params[0].value[1])
                    ? 'データなし'
                    : Number(params[0].value[1]).toFixed(2)) +
                  '<br/>' +
                  '----' +
                  '<br/>' +
                  t('上限') +
                  ':' +
                  (isNaN(params[2].value) ? 'データなし' : Number(params[2].value).toFixed(2)) +
                  '<br/>' +
                  t('下限') +
                  ':' +
                  (isNaN(params[3].value) ? 'データなし' : Number(params[3].value).toFixed(2))
                );
              }
            },
            dataZoom: [
              {
                type: 'inside'
              }
            ],

            xAxis: {
              type: 'category',
              data: source.xlist,
              axisLabel: {
                rotate: '15',
                fontSize: 10
              }
            },
            yAxis: {
              type: 'value'
            },
            series:
              searchType === SearchType.WORK
                ? [
                    {
                      name: t('最大値'),
                      type: 'line',
                      data: source.data_upper_distance,
                      color: red[500]
                    },
                    {
                      name: t('最小値'),
                      type: 'line',
                      data: source.data_lower_distance,
                      color: blue[500]
                    },
                    {
                      name: t('上限'),
                      type: 'line',
                      color: pink[500],
                      data: source.data_upper_limits,
                      symbolSize: 0,
                      lineStyle: {
                        type: 'dotted'
                      },
                      connectNulls: true // null を無視して線をつなぐ
                    },
                    {
                      name: t('下限'),
                      type: 'line',
                      color: lightBlue[500],
                      data: source.data_lower_limits,
                      connectNulls: true,
                      symbolSize: 0,
                      lineStyle: {
                        type: 'dotted'
                      }
                    }
                  ]
                : [
                    {
                      name: t('最大値箱ひげ図'),
                      type: 'boxplot',
                      data: boxData[0],
                      color: red[500]
                    },
                    {
                      name: t('最小値箱ひげ図'),
                      type: 'boxplot',
                      data: boxData[1],
                      color: blue[500]
                    },
                    {
                      name: t('上限'),
                      type: 'line',
                      color: pink[500],
                      data: source.data_upper_limits.map((e: any) => {
                        return e?.[0] ?? null;
                      }),
                      connectNulls: true,
                      symbolSize: 0,
                      lineStyle: {
                        type: 'dotted'
                      }
                    },
                    {
                      name: t('下限'),
                      type: 'line',
                      color: lightBlue[500],
                      data: source.data_lower_limits.map((e: any) => {
                        return e?.[0] ?? null;
                      }),
                      connectNulls: true,
                      symbolSize: 0,
                      lineStyle: {
                        type: 'dotted'
                      }
                    }
                  ]
          }}
          exportData={exportData}
          exportFields={exportFields}
          exportFilename={exportFilename}
          height={height ? height : '100px'}
          zoomCharts={zoomCharts ? zoomCharts : undefined}
          zoomChartsClose={zoomChartsClose ? zoomChartsClose : undefined}
          zoomChartId={zoomChartId ? zoomChartId : undefined}
          zoomStatus={zoomChartId === zoomedChartId}
        />
      </div>
    );
  }
);

LFCChartsCloudDistanceTrend.displayName = 'LFCChartsCloudDistanceTrend';

export default LFCChartsCloudDistanceTrend;
